import { useEffect, useRef } from 'react'

import {
  firebaseOnAuthStateChanged,
  firebaseSignOut,
  initFirebase,
} from '../lib/firebase'
import { selectFirebaseConfig } from '../store/common/selects'
import { selectUserStatus } from '../store/user/selects'
import { useBulkSelector } from './useBulkSelector'

const useSelectors = () =>
  useBulkSelector({
    firebaseConfig: selectFirebaseConfig,
    userStatus: selectUserStatus,
  })

interface Params {
  onAuth: (data: { token: string; email: string | null }) => void
}

export const useFirebaseAuth = ({ onAuth }: Params): void => {
  const { firebaseConfig, userStatus } = useSelectors()

  const hasAccount = !!userStatus?.account?.hasAccount

  const onAuthRef = useRef(onAuth)

  useEffect(() => {
    onAuthRef.current = onAuth
  }, [onAuth])

  useEffect(() => {
    if (!firebaseConfig || hasAccount) return undefined

    let unsubscribe: (() => void) | null = null
    let isCancelled = false

    const init = async () => {
      if (isCancelled) return

      initFirebase(firebaseConfig)
      await firebaseSignOut()

      if (isCancelled) return

      unsubscribe = firebaseOnAuthStateChanged(async (user) => {
        if (!user || isCancelled) return

        const token = await user.getIdToken()
        onAuthRef.current({ token, email: user.email })
      })

      if (isCancelled && unsubscribe) {
        unsubscribe()
        unsubscribe = null
      }
    }

    init()

    return () => {
      isCancelled = true
      unsubscribe?.()
    }
  }, [firebaseConfig, hasAccount])
}
